/**
 * Plan Detail Page
 * View details of a single waste management plan
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { planService } from '../services/plan.service';
import type { Plan, PlanData, PlanBidWithPricing, ServiceProvider, AcceptBidResponse } from '../types';
import {
  formatDateShort,
  formatPlanStatus,
  formatProjectType,
  formatWasteStreamType,
  formatDiversionMethod,
  formatWeight,
  formatPhoneNumber,
  getStatusColor,
} from '../utils/formatting';
import { ComplianceGauge } from '../components/compliance/ComplianceGauge';
import { DiversionCalculator } from '../components/compliance/DiversionCalculator';
import { ComplianceReport } from '../components/compliance/ComplianceReport';
import { ComplianceVerificationPanel } from '../components/compliance/ComplianceVerificationPanel';
import { BidCard } from '../components/plan/BidCard';
import { AcceptBidDialog } from '../components/plan/AcceptBidDialog';
import { OperationalRequirements } from '../components/plan/OperationalRequirements';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useAuth } from '@/contexts/AuthContext';
import { RegisterPromptDialog } from '@/components/auth/RegisterPromptDialog';

export function PlanDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [plan, setPlan] = useState<Plan | null>(null);
  const [planData, setPlanData] = useState<PlanData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    location.state?.message || null
  );
  const [hasJob, setHasJob] = useState<boolean | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [isCreatingJob, setIsCreatingJob] = useState(false);

  // Bid management state
  const [bids, setBids] = useState<(PlanBidWithPricing & { service_provider: ServiceProvider })[]>([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [bidToAccept, setBidToAccept] = useState<(PlanBidWithPricing & { service_provider: ServiceProvider }) | null>(null);
  const [acceptedBid, setAcceptedBid] = useState<(PlanBidWithPricing & { service_provider: ServiceProvider }) | null>(null);

  // Publish management state
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  // Authentication state
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);

  const loadBids = useCallback(async () => {
    if (!id) return;

    setBidsLoading(true);
    try {
      const response = await planService.getBids(id);
      setBids(response.bids);

      // Check if there's an accepted bid
      const accepted = response.bids.find(bid => bid.status === 'accepted');
      if (accepted) {
        setAcceptedBid(accepted);
      }
    } catch (err) {
      console.error('Failed to load bids:', err);
    } finally {
      setBidsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const loadPlan = async () => {
      if (!id) return;

      try {
        const fetchedPlan = await planService.getPlan(id);
        setPlan(fetchedPlan);
        setPlanData(planService.parsePlanData(fetchedPlan));

        // Check if job exists for this plan
        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}/api/v1/plans/${id}/job`
          );
          if (response.ok) {
            const data = await response.json();
            setHasJob(true);
            setJobId(data.job.id);
          } else {
            setHasJob(false);
          }
        } catch (err) {
          setHasJob(false);
        }

        // Load bids if plan is approved
        if (fetchedPlan.status === 'approved') {
          loadBids();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load plan');
      } finally {
        setIsLoading(false);
      }
    };

    loadPlan();
  }, [id, loadBids]);

  const handleAcceptBid = async (): Promise<AcceptBidResponse> => {
    if (!id || !bidToAccept) {
      throw new Error('Plan ID or bid information is missing');
    }

    // This function is now passed to AcceptBidDialog as onAccept prop
    // It returns the payment response instead of handling success directly
    const response = await planService.acceptBid(id, bidToAccept.id);
    return response;
  };

  const handlePaymentSuccess = () => {
    // Called by AcceptBidDialog after successful payment
    if (bidToAccept) {
      setAcceptedBid(bidToAccept);
    }
    setSuccessMessage('Payment successful! Job created.');
    setBidToAccept(null);

    // Reload bids to get updated statuses
    loadBids();
  };

  const handleCloseAcceptDialog = () => {
    setBidToAccept(null);
    // Reset any payment-related state
  };

  const handleRejectBid = async (bidId: string) => {
    if (!id) return;

    try {
      await planService.rejectBid(id, bidId);
      setSuccessMessage('Bid rejected successfully');

      // Reload bids to get updated statuses
      await loadBids();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject bid');
    }
  };

  const handlePublishPlan = async () => {
    if (!id) return;

    setIsPublishing(true);
    setError(null);

    try {
      const publishedPlan = await planService.publishPlan(id);
      setPlan(publishedPlan);
      setShowPublishDialog(false);
      setSuccessMessage('Plan successfully published to the marketplace! Service providers can now submit bids.');

      // Load bids now that plan is approved
      loadBids();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to publish plan';
      setError(message);
    } finally {
      setIsPublishing(false);
    }
  };

  const createJob = async () => {
    if (!plan || !planData) return;

    setIsCreatingJob(true);
    try {
      const jobPayload: {
        plan_id: string;
        project_name: string;
        project_address: string;
        start_date: string;
        target_completion_date?: string;
        target_diversion_rate: number;
        service_provider_id?: string;
        bid_id?: string;
      } = {
        plan_id: plan.id,
        project_name: planData.project_info.project_name,
        project_address: planData.project_info.project_address,
        start_date: new Date(planData.project_info.estimated_start_date).toISOString(),
        target_completion_date: planData.project_info.estimated_completion_date
          ? new Date(planData.project_info.estimated_completion_date).toISOString()
          : undefined,
        target_diversion_rate: 65,
      };

      // Include accepted bid info if available
      if (acceptedBid) {
        jobPayload.service_provider_id = acceptedBid.service_provider_id;
        jobPayload.bid_id = acceptedBid.id;
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jobPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create job');
      }

      const data = await response.json();
      const message = acceptedBid
        ? `Job created successfully with ${acceptedBid.service_provider.name}!`
        : 'Job created successfully! You can now share this with service providers.';

      navigate(`/jobs/${data.job.id}`, {
        state: { message },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create job');
      setIsCreatingJob(false);
    }
  };

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (isLoading) {
    return (
      <ErrorBoundary resetKeys={[id || '']}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-neutral-600">Loading plan...</p>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  if (error || !plan || !planData) {
    return (
      <ErrorBoundary resetKeys={[id || '']}>
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Plan Not Found</h2>
          <p className="text-neutral-600 mb-6">{error || 'The requested plan could not be found'}</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Home
          </button>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary resetKeys={[id || '']}>
      <div>
      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-accent-50 border border-accent-200 rounded-lg flex items-center">
          <svg
            className="w-5 h-5 text-accent-600 mr-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-accent-900">{successMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-neutral-900">
              {planData.project_info.project_name}
            </h1>
            {plan.status === 'approved' && !acceptedBid ? (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-accent-100 text-accent-800 flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-500"></span>
                </span>
                Live on Marketplace
              </span>
            ) : (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(plan.status)}`}>
                {formatPlanStatus(plan.status)}
              </span>
            )}
          </div>
          <p className="text-neutral-600">{planData.project_info.project_address}</p>
        </div>
        <div className="flex gap-3">
          {plan.status === 'draft' && (
            <Link to={`/plans/${plan.id}/edit`} className="btn-secondary">
              Edit Plan
            </Link>
          )}
          <button onClick={() => navigate('/')} className="btn-secondary">
            Home
          </button>
        </div>
      </div>

      {/* Sign in to Publish CTA - Show for unauthenticated users viewing draft plans */}
      {plan.status === 'draft' && !isAuthenticated && !acceptedBid && (
        <div className="card mb-6 border-2 border-accent-200 bg-gradient-to-br from-accent-50 to-white">
          <div className="card-body">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-accent-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-accent-900 mb-2">
                  Ready to Get Competitive Bids?
                </h3>
                <p className="text-sm text-accent-800 mb-3">
                  Sign in to publish your plan to the marketplace and receive competitive bids from verified service providers in your area.
                </p>
                <ul className="text-sm text-accent-700 mb-4 space-y-1">
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-accent-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Compare pricing from multiple providers
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-accent-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Track your project and compliance progress
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-accent-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Access this plan anytime from any device
                  </li>
                </ul>
                <button
                  onClick={() => setShowRegisterDialog(true)}
                  className="btn-primary bg-accent-600 hover:bg-accent-700 w-full sm:w-auto"
                >
                  <svg
                    className="w-5 h-5 mr-2 inline"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                    />
                  </svg>
                  Sign In to Publish
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publish to Marketplace - Show for draft plans owned by authenticated user */}
      {plan.status === 'draft' && isAuthenticated && user?.email === plan.applicant_email && !acceptedBid && (
        <div className="card mb-6 border-accent-200 bg-accent-50">
          <div className="card-body">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-accent-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-accent-900 mb-2">Publish to Marketplace</h3>
                <p className="text-accent-800 mb-4">
                  List your project on the marketplace to receive competitive bids from verified service providers in your area.
                </p>
                <button
                  onClick={() => setShowPublishDialog(true)}
                  className="btn-primary bg-accent-600 hover:bg-accent-700"
                >
                  <svg
                    className="w-5 h-5 mr-2 inline"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                    />
                  </svg>
                  Publish to Marketplace
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Job Creation / Link - Primary monetization feature */}
      {hasJob !== null && (
        <div className="card mb-6 border-primary-200 bg-primary-50">
          <div className="card-header bg-primary-100">
            <h3 className="card-title text-primary-900">Track Project & Get Bids</h3>
          </div>
          <div className="card-body">
            {hasJob ? (
              <>
                <p className="text-primary-900 mb-4">
                  This plan has an active job for tracking materials and managing service provider bids.
                </p>
                <button
                  onClick={() => navigate(`/jobs/${jobId}`)}
                  className="btn-primary"
                >
                  <svg
                    className="w-5 h-5 mr-2 inline"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  View Job
                </button>
              </>
            ) : (
              <>
                <p className="text-primary-900 mb-4">
                  Ready to start tracking your project? Create a job to:
                </p>
                <ul className="list-disc list-inside text-primary-900 mb-6 space-y-2">
                  <li>Share your plan with service providers and receive bids</li>
                  <li>Track materials as weight tickets are received</li>
                  <li>Monitor actual diversion rates vs. planned rates</li>
                  <li>Generate compliance reports with real data</li>
                </ul>
                <button
                  onClick={createJob}
                  disabled={isCreatingJob}
                  className="btn-primary"
                >
                  {isCreatingJob ? (
                    <>
                      <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                      Creating Job...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5 mr-2 inline"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Create Job
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Compliance Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ComplianceGauge
          diversionRate={planData.compliance.diversion_rate}
          totalGenerated={planData.compliance.total_waste_generated}
          totalDiverted={planData.compliance.total_waste_diverted}
          isCompliant={planData.compliance.meets_calgreen_requirement}
        />
        <DiversionCalculator wasteStreams={planData.waste_streams} />
      </div>

      {/* Project Information */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">Project Information</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-neutral-600 mb-1">Project Type</dt>
            <dd className="text-neutral-900 font-medium">
              {formatProjectType(planData.project_info.project_type)}
            </dd>
          </div>
          <div>
            <dt className="text-neutral-600 mb-1">Square Footage</dt>
            <dd className="text-neutral-900 font-medium">
              {planData.project_info.total_square_footage
                ? `${planData.project_info.total_square_footage.toLocaleString()} sq ft`
                : 'Not specified'}
            </dd>
          </div>
          <div>
            <dt className="text-neutral-600 mb-1">Start Date</dt>
            <dd className="text-neutral-900 font-medium">
              {formatDateShort(planData.project_info.estimated_start_date)}
            </dd>
          </div>
          <div>
            <dt className="text-neutral-600 mb-1">Completion Date</dt>
            <dd className="text-neutral-900 font-medium">
              {formatDateShort(planData.project_info.estimated_completion_date)}
            </dd>
          </div>
        </dl>
      </div>

      {/* Applicant Information */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">Applicant Information</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-neutral-600 mb-1">Contact Name</dt>
            <dd className="text-neutral-900 font-medium">{planData.applicant_info.contact_name}</dd>
          </div>
          <div>
            <dt className="text-neutral-600 mb-1">Contact Email</dt>
            <dd className="text-neutral-900 font-medium">{planData.applicant_info.contact_email}</dd>
          </div>
          <div>
            <dt className="text-neutral-600 mb-1">Contact Phone</dt>
            <dd className="text-neutral-900 font-medium">
              {formatPhoneNumber(planData.applicant_info.contact_phone)}
            </dd>
          </div>
          {planData.applicant_info.company_name && (
            <div>
              <dt className="text-neutral-600 mb-1">Company Name</dt>
              <dd className="text-neutral-900 font-medium">{planData.applicant_info.company_name}</dd>
            </div>
          )}
          {planData.applicant_info.license_number && (
            <div>
              <dt className="text-neutral-600 mb-1">License Number</dt>
              <dd className="text-neutral-900 font-medium">{planData.applicant_info.license_number}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Waste Streams */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">
          Waste Streams ({planData.waste_streams.length})
        </h2>
        <div className="space-y-4">
          {planData.waste_streams.map((stream, index) => (
            <div key={index} className="p-4 bg-neutral-50 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-neutral-900">
                  {formatWasteStreamType(stream.material_type)}
                </h3>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    stream.diversion_method === 'landfill'
                      ? 'bg-neutral-200 text-neutral-700'
                      : 'bg-accent-100 text-accent-700'
                  }`}
                >
                  {formatDiversionMethod(stream.diversion_method)}
                </span>
              </div>
              <div className="text-sm text-neutral-600 space-y-1">
                <div>Estimated Quantity: {formatWeight(stream.estimated_quantity)}</div>
                {stream.facility_name && <div>Facility: {stream.facility_name}</div>}
                {stream.facility_address && <div>Address: {stream.facility_address}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Post-Construction Operational Requirements */}
      <OperationalRequirements
        requirements={planData.operational_requirements}
        projectName={planData.project_info.project_name}
        projectType={formatProjectType(planData.project_info.project_type)}
        squareFootage={planData.project_info.total_square_footage}
      />

      {/* Compliance Verification Panel */}
      <div className="mb-6">
        <ComplianceVerificationPanel plan={plan} />
      </div>

      {/* Compliance Report Download */}
      <div className="mb-6">
        <ComplianceReport plan={plan} />
      </div>

      {/* Bids Section - Only show for approved plans */}
      {plan.status === 'approved' && (
        <div className="card mb-6">
          <div className="card-header">
            <h2 className="card-title">Service Provider Bids</h2>
          </div>
          <div className="card-body">
            {bidsLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-3"></div>
                <p className="text-neutral-600">Loading bids...</p>
              </div>
            ) : bids.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Bids Yet</h3>
                <p className="text-neutral-600 max-w-md mx-auto">
                  Service providers in your area will see this plan and can submit bids. Check back soon!
                </p>
              </div>
            ) : (
              <>
                {/* Show accepted bid with create job CTA if no job exists */}
                {acceptedBid && !hasJob && (
                  <div className="mb-6 p-4 bg-accent-50 border-2 border-accent-300 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-accent-900 mb-1">
                          Bid Accepted from {acceptedBid.service_provider.name}
                        </h3>
                        <p className="text-accent-800 mb-3">
                          Ready to start your project? Create a job to track materials and manage this project.
                        </p>
                        <button
                          onClick={createJob}
                          disabled={isCreatingJob}
                          className="btn-primary bg-accent-600 hover:bg-accent-700"
                        >
                          {isCreatingJob ? (
                            <>
                              <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                              Creating Job...
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-5 h-5 mr-2 inline"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 4v16m8-8H4"
                                />
                              </svg>
                              Create Job with {acceptedBid.service_provider.name}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bid cards */}
                <div className="space-y-4">
                  {bids.map((bid) => (
                    <BidCard
                      key={bid.id}
                      bid={bid}
                      planId={plan.id}
                      onAccept={() => setBidToAccept(bid)}
                      onReject={() => handleRejectBid(bid.id)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Accept Bid Dialog */}
      {bidToAccept && (
        <AcceptBidDialog
          bid={bidToAccept}
          isOpen={!!bidToAccept}
          onAccept={handleAcceptBid}
          onClose={handleCloseAcceptDialog}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}

      {/* Publish Confirmation Dialog */}
      {showPublishDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => !isPublishing && setShowPublishDialog(false)}
          ></div>

          {/* Dialog */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-12 w-12 rounded-full bg-accent-100 flex items-center justify-center">
                    <svg
                      className="h-6 w-6 text-accent-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                      />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold text-neutral-900">
                      Publish to Marketplace?
                    </h3>
                  </div>
                </div>
                {!isPublishing && (
                  <button
                    onClick={() => setShowPublishDialog(false)}
                    className="text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Content */}
              <div className="mb-6">
                {/* Info Box */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-primary-900 mb-2">What happens next:</h4>
                  <ul className="text-sm text-primary-800 space-y-2">
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Your plan will be visible to verified service providers in your area</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Service providers can submit competitive bids for your project</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>You can review and accept the best bid for your needs</span>
                    </li>
                  </ul>
                </div>

                {/* Warning */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-semibold text-yellow-800">Important</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        Plan details cannot be edited after publishing. Make sure all information is accurate.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Requirements */}
                <div className="bg-neutral-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-neutral-900 mb-2">Requirements:</h4>
                  <ul className="text-sm text-neutral-700 space-y-1">
                    <li className="flex items-center">
                      <svg className="h-4 w-4 text-accent-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      At least one material entry
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 text-accent-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Target diversion rate of at least 65%
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 text-accent-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Complete project information
                    </li>
                  </ul>
                </div>

                {/* Error Display */}
                {error && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-red-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowPublishDialog(false)}
                  disabled={isPublishing}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePublishPlan}
                  disabled={isPublishing}
                  className="btn-primary bg-accent-600 hover:bg-accent-700"
                >
                  {isPublishing ? (
                    <>
                      <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                      Publishing...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5 mr-2 inline"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Publish to Marketplace
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="mt-6 text-sm text-neutral-600 text-center">
        <p>
          Created: {formatDateShort(plan.created_at)} | Last Updated:{' '}
          {formatDateShort(plan.updated_at)}
        </p>
      </div>

      {/* Registration Dialog */}
      <RegisterPromptDialog
        isOpen={showRegisterDialog}
        onClose={() => setShowRegisterDialog(false)}
        projectId={id}
      />
    </div>
    </ErrorBoundary>
  );
}